import React, { useState, useEffect } from 'react';
import { useClient } from '../../context/ClientContext';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

function getHourLabel(h) {
  if (h === 0) return '12a';
  if (h < 12) return `${h}a`;
  if (h === 12) return '12p';
  return `${h - 12}p`;
}

export default function PerformanceHeatmap({ dateRange }) {
  const { brand, fetcher } = useClient();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState('conversions');

  useEffect(() => {
    setLoading(true);
    fetcher.fetchHourlyPerformance(dateRange)
      .then(setData)
      .finally(() => setLoading(false));
  }, [dateRange]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="h-48 bg-gray-200 rounded"></div>
      </div>
    );
  }

  if (!data?.heatmapData?.length) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide"
            style={{ fontFamily: brand.typography?.headerFont }}>
          When Your Ads Perform Best
        </h3>
        <p className="text-gray-500 mt-2">No hourly data available yet.</p>
      </div>
    );
  }

  const lookup = {};
  let maxVal = 0;
  for (const cell of data.heatmapData) {
    const val = cell[metric] || 0;
    lookup[`${cell.day}-${cell.hour}`] = val;
    if (val > maxVal) maxVal = val;
  }

  const primaryColor = brand.colors?.primary || '#2563eb';

  function getCellStyle(val) {
    if (maxVal === 0 || val === 0) return { backgroundColor: '#f9fafb' };
    const intensity = val / maxVal;
    const r = parseInt(primaryColor.slice(1, 3), 16);
    const g = parseInt(primaryColor.slice(3, 5), 16);
    const b = parseInt(primaryColor.slice(5, 7), 16);
    return {
      backgroundColor: `rgba(${r}, ${g}, ${b}, ${Math.max(0.1, intensity * 0.9)})`
    };
  }

  const displayHours = HOURS.filter((_, i) => i % 3 === 0);

  return (
    <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide"
              style={{ fontFamily: brand.typography?.headerFont }}>
            When Your Ads Perform Best
          </h3>
          <p className="text-xs text-gray-400 mt-1">By hour and day of week</p>
        </div>
        <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5">
          {[
            { key: 'conversions', label: 'Conv.' },
            { key: 'clicks', label: 'Clicks' }
          ].map(opt => (
            <button
              key={opt.key}
              onClick={() => setMetric(opt.key)}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                metric === opt.key ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[600px]">
          <div className="flex ml-10">
            {HOURS.map(h => (
              <div key={h} className="flex-1 text-center">
                <span className={`text-[10px] text-gray-400 ${displayHours.includes(h) ? '' : 'hidden sm:inline'}`}>
                  {getHourLabel(h)}
                </span>
              </div>
            ))}
          </div>

          {DAYS.map(day => (
            <div key={day} className="flex items-center gap-1 mb-0.5">
              <span className="text-xs text-gray-500 w-9 text-right flex-shrink-0">{day}</span>
              <div className="flex flex-1 gap-0.5">
                {HOURS.map(hour => {
                  const val = lookup[`${day}-${hour}`] || 0;
                  return (
                    <div
                      key={hour}
                      className="flex-1 aspect-square rounded-sm cursor-default transition-transform hover:scale-110"
                      style={getCellStyle(val)}
                      title={`${day} ${getHourLabel(hour)}: ${val} ${metric}`}
                    />
                  );
                })}
              </div>
            </div>
          ))}

          <div className="flex items-center justify-end gap-2 mt-3">
            <span className="text-[10px] text-gray-400">Less</span>
            {[0.1, 0.3, 0.5, 0.7, 0.9].map(intensity => {
              const r = parseInt(primaryColor.slice(1, 3), 16);
              const g = parseInt(primaryColor.slice(3, 5), 16);
              const b = parseInt(primaryColor.slice(5, 7), 16);
              return (
                <div
                  key={intensity}
                  className="w-3 h-3 rounded-sm"
                  style={{ backgroundColor: `rgba(${r}, ${g}, ${b}, ${intensity})` }}
                />
              );
            })}
            <span className="text-[10px] text-gray-400">More</span>
          </div>
        </div>
      </div>
    </div>
  );
}
